import { __assign, __spreadArray, __read, __rest } from 'tslib';
import { useTheme as useTheme$1 } from '@emotion/react';
import { textCrop } from '../utils/textCrop.js';
import get from 'lodash.get';
import { css } from '@compono/ui-theme';
import { mergeThemeList } from '../utils/mergeTheme.js';
import { getForwardProps } from '../utils/getForwardProps.js';
import { getVariants } from '../utils/getVariants.js';

/**
 * useTheme
 *
 * returns an object containing our theme and a range of functions for working with the theme.
 */
function useTheme() {
    var theme = useTheme$1();
    /**
     * applyTheme
     *
     * converts theme scale values to css values from the theme
     *
     * returns a single css style object
     *
     * @param style[] - style objects with css or theme scale values
     */
    function applyTheme(style) {
        var moreStyles = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            moreStyles[_i - 1] = arguments[_i];
        }
        if (moreStyles === null || moreStyles === void 0 ? void 0 : moreStyles.length) {
            return css(mergeStyles(__spreadArray([style], __read(moreStyles), false)))(theme);
        }
        return style ? css(prepareStyleObject(style))(theme) : {};
    }
    /**
     * getVariants
     *
     * returns an array of CSSObjects which make up a collection of variant styles from
     * a top-level theme object.
     *
     * see utils/getVariants.ts
     */
    function getVariants$1(themeKey, variants, order) {
        return getVariants(theme, themeKey, variants, order);
    }
    /**
     * mergeStyles
     *
     * merge two or more style objects
     */
    function mergeStyles(styles) {
        var clean = styles.filter(Boolean);
        var prepared = clean.map(prepareStyleObject);
        return mergeThemeList.apply(void 0, __spreadArray([], __read(prepared), false));
    }
    /**
     * prepareStyleObject
     *
     * convert `font` property in a style object to individual font properties (eg fontSize)
     *
     * eg { font: "title" } => { fontFamily: "DM Sans", fontSize: "1rem", fontWeight: "bold "}
     *
     * We need to apply the theme to the `font` property in a style object before merging that
     * object with other style objects so that any font part properties used in the other style
     * objects are handled correctly.
     *
     * If the object contains both a `font` property and font part properties, such as `fontSize`
     * then the part properties override those parts generated from the `font` property.
     *
     * @param styles - an array of style objects
     */
    function prepareStyleObject(style) {
        var obj = typeof style === "function" ? style(theme) : style;
        var font = obj.font, rest = __rest(obj, ["font"]);
        if (font) {
            var resolvedFont = css({ font: font })(theme).font;
            if (resolvedFont && typeof resolvedFont === "object") {
                return __assign(__assign({}, resolvedFont), rest);
            }
            return __assign({ font: resolvedFont }, rest);
        }
        return rest;
    }
    /**
     * handleStyleProps
     *
     * processes and filters a component's props returning props to be set on an html or svg element.
  
     * The value of the css key in the return object is a CSSObject and is expected to be handled by
     * Emotion's css prop handling (which converts the style object to a class name and sets it as
     * the value of the element's className property).
     *
     * @param props - component props
     * @param options
     * @param options.baseStyle - a theme style object
     * @param options.themeKey - a top-level key of the theme
     * @param options.variantKeys - an object specifying the variant for each variant type. **DEPRECATED** - will be overridden by `variantOrder` in themes that provide it.
     */
    function handleStyleProps(_a, options) {
        var _b = _a.sx, sx = _b === void 0 ? {} : _b, themeKeyProp = _a.themeKey, props = __rest(_a, ["sx", "themeKey"]);
        var _c = options || {}, _d = _c.baseStyle, baseStyle = _d === void 0 ? {} : _d, themeKeyOpt = _c.themeKey, variantKeys = _c.variantKeys;
        var themeKey = themeKeyProp || themeKeyOpt;
        var componentTheme = get(theme, "components.".concat(themeKey));
        var variantStyles = getVariants$1(themeKey, props, variantKeys);
        var finalCss = applyTheme.apply(void 0, __spreadArray(__spreadArray([baseStyle], __read(variantStyles), false), [sx], false));
        return __assign({ css: finalCss }, getForwardProps(props, (componentTheme === null || componentTheme === void 0 ? void 0 : componentTheme.variantOrder) || variantKeys));
    }
    var textCrop$1 = function (args) {
        return textCrop(__assign(__assign({}, theme.textCropConfig), args));
    };
    return {
        theme: theme,
        applyTheme: applyTheme,
        getVariants: getVariants$1,
        handleStyleProps: handleStyleProps,
        mergeStyles: mergeStyles,
        prepareStyleObject: prepareStyleObject,
        textCrop: textCrop$1,
    };
}

export { useTheme };
//# sourceMappingURL=useTheme.js.map
